// Bulk resident invites from a spreadsheet (CSV), like the unit import: the
// admin downloads a template, fills one resident per row with their unit, and
// uploads it back. Pure functions here; the invite dialog sends one invitation
// per valid row.

import { csvFile } from './annual-export';
import { headerKey, parseCsv } from './csv-import';
import { unitAddress } from './unit-address';

export interface InviteUnit {
  id: string;
  block: string;
  number: string;
}

export interface ResidentImportRow {
  /** Line in the file, counting the header as line 1. */
  line: number;
  name: string;
  email: string;
  block: string;
  number: string;
  /** The unit the row points at, once found. */
  unitId?: string;
  /** An i18n key and its values when the row can't be sent. */
  error?: { key: string; vars?: Record<string, string | number> };
}

export type ResidentImportParse =
  | { ok: true; rows: ResidentImportRow[] }
  | { ok: false; error: 'empty' | 'missingColumns' };

type Column = 'name' | 'email' | 'block' | 'number';

// Template headers (Indonesian), with English ones accepted too.
const COLUMNS: Record<string, Column> = {
  nama: 'name',
  namalengkap: 'name',
  name: 'name',
  fullname: 'name',
  email: 'email',
  alamatemail: 'email',
  blok: 'block',
  blokjalan: 'block',
  block: 'block',
  nomor: 'number',
  nomorunit: 'number',
  no: 'number',
  number: 'number',
};

// The same limits as the invite form's API.
const MAX_NAME = 100;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const unitKey = (block: string, number: string) => `${block.trim().toLowerCase()}|${number.trim().toLowerCase()}`;

/** The template: the header and two example rows. */
export function residentTemplateCsv(): string {
  return csvFile([
    ['Nama lengkap', 'Email', 'Blok / Jalan', 'Nomor'],
    ['Budi Santoso', 'budi@example.com', 'A2', '18'],
    ['Siti Rahma', 'siti@example.com', 'Matahari', '5'],
  ]);
}

/**
 * Reads an import file and checks every row against the cluster's units and
 * the emails already in it; rows with problems carry an error.
 */
export function parseResidentImport(text: string, units: InviteUnit[], existingEmails: Iterable<string>): ResidentImportParse {
  const [head, ...body] = parseCsv(text);
  const isBlank = (r: string[]) => r.every((c) => c.trim() === '');
  if (!head || body.every(isBlank)) return { ok: false, error: 'empty' };

  const header = head.map((h) => COLUMNS[headerKey(h)]);
  if (!header.includes('name') || !header.includes('email') || !header.includes('number')) {
    return { ok: false, error: 'missingColumns' };
  }

  const byAddress = new Map(units.map((u) => [unitKey(u.block, u.number), u.id]));
  const taken = new Set([...existingEmails].map((e) => e.trim().toLowerCase()));
  const seen = new Map<string, number>();
  const rows: ResidentImportRow[] = [];

  for (const [i, raw] of body.entries()) {
    if (isBlank(raw)) continue;
    const line = i + 2;
    const get = (col: Column) => {
      const at = header.indexOf(col);
      return at >= 0 ? (raw[at] ?? '').trim() : '';
    };
    const row: ResidentImportRow = { line, name: get('name'), email: get('email'), block: get('block'), number: get('number') };
    const email = row.email.toLowerCase();
    const unitId = byAddress.get(unitKey(row.block, row.number));

    if (!row.name) row.error = { key: 'res.import.errName' };
    else if (row.name.length > MAX_NAME) row.error = { key: 'res.import.errNameLong', vars: { max: MAX_NAME } };
    else if (!EMAIL.test(row.email)) row.error = { key: 'res.import.errEmail' };
    else if (!row.number) row.error = { key: 'res.import.errNoUnit' };
    else if (!unitId) row.error = { key: 'res.import.errUnknownUnit', vars: { unit: unitAddress(row.block, row.number) } };
    else if (taken.has(email)) row.error = { key: 'res.import.errEmailExists' };
    else if (seen.has(email)) row.error = { key: 'res.import.errEmailDuplicate', vars: { line: seen.get(email)! } };

    if (!row.error) {
      row.unitId = unitId;
      seen.set(email, line);
    }
    rows.push(row);
  }
  return { ok: true, rows };
}
